import { useState } from "react";
import { GroupChange, PageInfo } from "@models/ISchema";
import { useApolloClient } from "@apollo/client";
import { groupChangesRequest } from "../graphql/groupChangesRequest.query";
import { GroupsTableFiltersState } from "../context/useGroupsTableFilters";

type Response = [
  GroupChange[],
  boolean,
  PageInfo,
  (params: {
    page: number;
    pageSize?: number;
    owned: boolean;
    groupId: string;
    orderBy: GroupsTableFiltersState["orderBy"];
    searchBy: GroupsTableFiltersState["searchBy"];
    externalFields?: Record<string, any>;
  }) => void,
];
export const useGetGroupsChanges = (scenarioId: string, originId: string): Response => {
  const client = useApolloClient();
  const [response, setResponse] = useState<GroupChange[]>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo>(null);
  const [loading, setLoading] = useState(false);

  const buildFieldsToFilterBy = (groupId: string, owned: boolean) => {
    const fields: Record<string, any> = {};

    if (groupId) {
      fields.groupId = { eq: groupId };
    }

    if (owned) {
      fields.isOwner = { is: owned };
    }

    return fields;
  };

  const getGroupChanges = async ({
    page,
    pageSize = 20,
    owned,
    groupId,
    orderBy,
    searchBy,
    externalFields,
  }: {
    page: number;
    pageSize?: number;
    owned: boolean;
    groupId: string;
    orderBy: GroupsTableFiltersState["orderBy"];
    searchBy: GroupsTableFiltersState["searchBy"];
    externalFields?: Record<string, any>;
  }) => {
    setLoading(true);
    const fields = {
      ...buildFieldsToFilterBy(groupId, owned),
      ...(externalFields ?? {}),
    };

    try {
      const { data, errors } = await client.query({
        query: groupChangesRequest,
        variables: {
          scenarioId,
          originId,
          filter: {
            fields: Object.keys(fields).length === 0 ? undefined : fields,
            pagination: {
              page,
              size: pageSize,
            },
            orderBy:
              !orderBy || Object.keys(orderBy).length === 0 || orderBy?.field == null
                ? undefined
                : (() => {
                    const orderByWithoutHeader = { ...orderBy };
                    delete orderByWithoutHeader.header;
                    return orderByWithoutHeader;
                  })(),
            searchBy:
              !searchBy || Object.keys(searchBy).length === 0 || searchBy?.text == null
                ? undefined
                : searchBy,
          },
        },
        fetchPolicy: "no-cache",
      });

      if (errors) {
        setLoading(false);
        setPageInfo({});
        setResponse([]);
        console.error(errors);
        return null;
      }

      const groupChanges = data?.cube?.groupsManagerGroupChanges?.items;
      const newPageInfo = data?.cube?.groupsManagerGroupChanges?.pageInfo;

      setResponse(groupChanges ?? []);
      setPageInfo(newPageInfo);
    } catch (error) {
      setPageInfo({});
      setResponse([]);
      console.error(error);
    }

    setLoading(false);
  };

  return [response, loading, pageInfo, getGroupChanges];
};
